import filter from 'lodash/filter.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import values from 'lodash/values.js';

import ActivitySubmission from '@/models/ActivitySubmission.js';
import Course from '@/models/Course.js';
import CourseTemplate from '@/models/CourseTemplate.js';

export default {
    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    computed: {
        activityId() {
            return Number(this.$route.params.activityId);
        },

        activity() {
            if (this.readOnly) return this.$store.getters[`${this.namespace}/get`](this.uuid);

            return this.$store.getters[`${this.namespace}/getByKey`]({
                keyName: 'id',
                key: this.activityId,
            });
        },

        isCourseTemplate() {
            return this.$route.name.includes('template');
        },

        // Callers read only scalar fields and pass the course to policies, which load their
        // own relations. withAll() cost about 30 ms per evaluation on a large course (P6).
        course() {
            return this.isCourseTemplate
                ? CourseTemplate.query().find(this.$route.params.id)
                : Course.query().find(this.$route.params.courseId);
        },

        activityContent() {
            return this.get('activity_content');
        },

        allowsMultipleSubmissions() {
            return this.maxAttempts > 1;
        },

        maxAttempts() {
            return this.activity.activity_settings.max_attempts_allowed;
        },

        latestUserSubmission() {
            return this.$store.getters['ActivitySubmissions/latestUserSubmission'](this.activityId);
        },

        media() {
            return this.get('media');
        },

        disabled() {
            if (this.submitBlockedByExistingGrade) {
                return true;
            }

            if (this.usedAllAttemptAuthorizations) {
                return true;
            }

            if (this.userHasAuthorizedAttempt) {
                return false;
            }

            return (this.isLate && !this.canSubmitLate) || !this.hasPermissionToSubmit;
        },

        completed() {
            // Completion is per-user: key off the logged-in user's OWN submissions, not the
            // (possibly all-users) `submissions` list a viewOther instructor/admin sees.
            return this.userSubmissions.length > 0 || get(this.activity, 'completed');
        },

        submissions() {
            return this.$gate.allows('viewOther', ['ActivitySubmission', this.course])
                ? this.allSubmissions
                : this.userSubmissions;
        },

        userSubmissions() {
            const submissions = !isEmpty(this.provideSubmissions)
                ? this.provideSubmissions
                : values(this.$store.getters['ActivitySubmissions/all']());

            return filter(
                submissions,
                submission =>
                    get(submission, 'created_by') === this.$store.getters['Auth/userId'] &&
                    get(submission, 'activity_id') === this.activityId
            );
        },

        allSubmissions() {
            const submissions = !isEmpty(this.provideSubmissions)
                ? this.provideSubmissions
                : values(this.$store.getters['ActivitySubmissions/all']());

            return filter(submissions, submission => get(submission, 'activity_id') === this.activityId);
        },

        settings() {
            return this.namespace.toLowerCase().includes('temp') ? 'activity_settings_template' : 'activity_settings';
        },

        userGrades() {
            return values(this.$store.getters['GradebookGrades/all']?.() ?? []).filter(
                grade => grade.activity_id === this.activityId && grade.user_id === this.$store.getters['Auth/userId']
            );
        },

        hasExistingGrade() {
            return this.userGrades.length > 0;
        },

        submitBlockedByExistingGrade() {
            return this.hasExistingGrade && !this.userHasAuthorizedAttempt;
        },

        // Show Submit when: unused authorized re-attempt (even if already completed),
        // blocked-by-grade (disabled button + tooltip), or not yet completed.
        // Do not early-return on completed alone — that made userHasAuthorizedAttempt dead.
        canShowSubmit() {
            if (this.readOnly) {
                return false;
            }

            return this.userHasAuthorizedAttempt || this.submitBlockedByExistingGrade || !this.completed;
        },

        enrollment() {
            return this.$store.state.ActiveEnrollment.enrollment;
        },

        enrollmentPermissions() {
            return this.$store.getters['ActiveEnrollment/permissions'];
        },

        canViewSubmissions() {
            return (
                this.$gate.allows('viewAny', ['ActivitySubmission', this.course]) ||
                this.$gate.allows('viewOther', ['ActivitySubmission', this.course])
            );
        },

        canSubmitActivity() {
            return this.$gate.allows('create', ['ActivitySubmission', this.course]);
        },

        hasPermissionToSubmit() {
            return this.$can('create', 'activity submission') && !this.instructor;
        },

        instructor() {
            const roles = this.$store.getters['ActiveEnrollment/roles'];

            if (!roles) return false;

            return roles.some(role => role.name === 'Instructor');
        },

        admin() {
            return this.$store.getters['User/hasRole']('Admin');
        },
    },

    data: () => ({
        isLoading: false,
        submissionsLoading: true,
        // Components that drive their own initial submissions fetch (e.g. via an
        // immediate activityId watcher) set this to opt out of the mixin's
        // created() fetch and avoid a duplicate first-load request.
        ownsSubmissionsFetch: false,
    }),

    created() {
        if (!this.ownsSubmissionsFetch && !this.readOnly && isEmpty(this.submissions)) {
            this.fetchAuthUserSubmissions();
        }
    },

    methods: {
        get(field) {
            return this.$store.getters[`${this.namespace}/${this.uuid}/get`](field);
        },

        verifyActivity() {
            return this.$store.dispatch('Activities/verifyActivity', {
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
            });
        },

        async fetchSubmissions() {
            const filters = { activityId: this.activityId };

            if (!this.$gate.allows('viewOther', ['ActivitySubmission', this.course])) {
                filters.createdBy = this.$store.getters['Auth/userId'];
            }

            await this.fetchIntoSubmissionStore(filters);

            this.$emit('fetch-submissions', () => {
                this.submissionsLoading = false;
            });
        },

        async fetchAuthUserSubmissions() {
            const filters = { activityId: this.activityId };

            filters.createdBy = this.$store.getters['Auth/userId'];

            await this.fetchIntoSubmissionStore(filters);

            this.$emit('fetch-submissions', () => {
                this.submissionsLoading = false;
            });
        },

        /**
         * Quiz and survey pages fetch here too. Quiz.vue only fills provide-submissions for
         * learners who can view quiz attempts, so the rest read their rows from this store.
         */
        async fetchIntoSubmissionStore(filters) {
            const data = await this.$store.dispatch('ActivitySubmissions/fetch', filters);

            await ActivitySubmission.insert({ data });
        },

        async submit() {
            this.isLoading = true;

            try {
                await this.$store.dispatch(`${this.namespace}/completeActivity`, this.payload);

                this.$store.commit(`Activities/${this.uuid}/EDIT_FIELD`, {
                    field: 'completed',
                    value: this.$moment.utc().format('YYYY-MM-DD HH:mm:ss'),
                });
            } catch (error) {
                this.$handleResponseError(error);
            }

            this.isLoading = false;
        },

        toggleSubmissionLoading(finishLoading) {
            return finishLoading();
        },

        isEmpty,
    },
};
